import { IServiceUserContent } from "../../../../datasolvr/ServicesFacade";
import { planEatApi } from "../../../../planEatApi/PlanEatApi";
import { Account, AdminFor, GROUPS, normalizeGroups } from "../../accounts";
import { SysadminData } from "../../SysadminData";
import { errorMessage } from "../../sysadminUtils";

/** Groups that are set directly on the user. The manager groups are derived from "Admin for". */
export const MANUAL_GROUPS = [GROUPS.eater, GROUPS.kitchenManager, GROUPS.systemAdmin, GROUPS.unverified];

export const DERIVED_GROUP: Record<keyof AdminFor, string> = {
    companies: GROUPS.companyManager,
    offices: GROUPS.officeManager,
    eaterGroups: GROUPS.eaterGroupAdmin,
};

export interface UserForm {
    companyId: string;
    officeId: string;
    eaterGroupId: string;
    /** Selected manual groups. */
    groups: string[];
    adminFor: AdminFor;
}

/** The form state matching the account as it is now. */
export function initialForm(account: Account, data: SysadminData): UserForm {
    const officeId = account.user?.officeId ?? "";
    return {
        companyId: data.companyIdOfOffice(officeId) ?? "",
        officeId,
        eaterGroupId: account.user?.eaterGroupId ?? "",
        groups: account.groups.filter(g => MANUAL_GROUPS.includes(g)),
        adminFor: account.adminFor,
    };
}

const sorted = (values: string[]) => [...values].sort();
const sameSet = (a: string[], b: string[]) => JSON.stringify(sorted(a)) === JSON.stringify(sorted(b));

/**
 * Applies the changes made in the form (compared to `original`) to the user's current groups.
 * Only groups the sysadmin actually changed are touched, so unrelated (legacy) groups are kept as they are.
 */
export function resultingGroups(current: string[], original: UserForm, form: UserForm): string[] {
    let result = normalizeGroups(current);
    const set = (group: string, present: boolean) => {
        result = result.filter(g => g !== group);
        if (present) result.push(group);
    };
    MANUAL_GROUPS.forEach(group => {
        const wanted = form.groups.includes(group);
        if (wanted !== original.groups.includes(group)) set(group, wanted);
    });
    (Object.keys(DERIVED_GROUP) as (keyof AdminFor)[]).forEach(kind => {
        if (!sameSet(form.adminFor[kind], original.adminFor[kind])) set(DERIVED_GROUP[kind], form.adminFor[kind].length > 0);
    });
    return result;
}

async function postAccountGroups(content: IServiceUserContent, groups: string[]) {
    // The content is posted back as fetched (hash included), which keeps the password unchanged
    await planEatApi.postServiceUser({ ...content, data_raw: { ...content.data_raw!, groups } });
}

/**
 * The backend adds eater_group_admin_users to existing eater group admins when an eater group is saved,
 * but stores their groups joined into one string. Adding the group beforehand avoids that.
 */
export async function prepareEaterGroupAdmins(adminIds: string[], skip = "") {
    for (const adminId of adminIds.filter(id => id !== skip)) {
        let content: IServiceUserContent;
        try {
            content = (await planEatApi.getServiceUser(adminId)).data.content;
        } catch {
            continue;
        }
        const groups = normalizeGroups(content.data_raw?.groups);
        if (!groups.includes(GROUPS.eaterGroupAdmin)) {
            await postAccountGroups(content, [...groups, GROUPS.eaterGroupAdmin]);
        }
    }
}

const withAdmin = (ids: string[] | undefined, email: string, add: boolean) =>
    add ? Array.from(new Set([...(ids ?? []), email])) : (ids ?? []).filter(id => id !== email);

async function setAdmin(kind: keyof AdminFor, id: string, email: string, add: boolean) {
    if (kind === "companies") {
        const content = (await planEatApi.getCompany(id)).data.content;
        await planEatApi.postCompany({ ...content, companyAdminIds: withAdmin(content.companyAdminIds, email, add) });
    } else if (kind === "offices") {
        const content = (await planEatApi.getOffice(id)).data.content;
        await planEatApi.postOffice({ ...content, officeAdminIds: withAdmin(content.officeAdminIds, email, add) });
    } else {
        const content = (await planEatApi.getEaterGroup(id)).data.content;
        await prepareEaterGroupAdmins(content.eaterGroupAdminIds ?? [], email);
        await planEatApi.postEaterGroup({ ...content, eaterGroupAdminIds: withAdmin(content.eaterGroupAdminIds, email, add) });
    }
}

/** Changes the account's user groups based on the latest stored groups. Also repairs groups stored in a broken format. */
export async function updateAccountGroups(email: string, change: (groups: string[]) => string[]) {
    const content = (await planEatApi.getServiceUser(email)).data.content;
    const current = content.data_raw?.groups ?? [];
    const groups = change(normalizeGroups(current));
    if (JSON.stringify(groups) !== JSON.stringify(current)) {
        await postAccountGroups(content, groups);
    }
}

export interface SaveStep {
    label: string;
    run: () => Promise<void>;
}

/** The steps needed to save the form. The account's groups are saved last so they end up consistent. */
export function saveSteps(account: Account, original: UserForm, form: UserForm, names: (kind: keyof AdminFor, id: string) => string): SaveStep[] {
    const email = account.email;
    const steps: SaveStep[] = [];

    if (form.officeId !== original.officeId || form.eaterGroupId !== original.eaterGroupId) {
        steps.push({
            label: "update placement",
            run: async () => {
                if (account.user) {
                    const content = (await planEatApi.getUser(account.user.id)).data.content;
                    await planEatApi.postUser({ ...content, officeId: form.officeId, eaterGroupId: form.eaterGroupId });
                } else {
                    await planEatApi.postUser({ id: "", name: "", description: "", applicationUserId: email, officeId: form.officeId, eaterGroupId: form.eaterGroupId });
                }
            },
        });
    }

    (Object.keys(DERIVED_GROUP) as (keyof AdminFor)[]).forEach(kind => {
        // Additions before removals, so an entity is never left without admins halfway through
        form.adminFor[kind].filter(id => !original.adminFor[kind].includes(id)).forEach(id =>
            steps.push({ label: `make admin of ${names(kind, id)}`, run: () => setAdmin(kind, id, email, true) }));
    });
    (Object.keys(DERIVED_GROUP) as (keyof AdminFor)[]).forEach(kind => {
        original.adminFor[kind].filter(id => !form.adminFor[kind].includes(id)).forEach(id =>
            steps.push({ label: `remove as admin of ${names(kind, id)}`, run: () => setAdmin(kind, id, email, false) }));
    });

    steps.push({
        label: "update user groups",
        run: async () => {
            // Fetched after the entities are saved, as the backend also changes manager groups
            const content = (await planEatApi.getServiceUser(email)).data.content;
            const current = content.data_raw?.groups ?? [];
            const groups = resultingGroups(current, original, form);
            if (JSON.stringify(groups) !== JSON.stringify(current)) {
                await postAccountGroups(content, groups);
            }
        },
    });
    return steps;
}

/** Runs the steps in order and stops at the first failure. Every step can safely be repeated. */
export async function runSteps(steps: SaveStep[]): Promise<{ done: string[]; failed?: { label: string; message: string } }> {
    const done: string[] = [];
    for (const step of steps) {
        try {
            await step.run();
            done.push(step.label);
        } catch (e) {
            return { done, failed: { label: step.label, message: errorMessage(e) } };
        }
    }
    return { done };
}
